import type {
	DataProcessingServiceConsent,
	UC_UI_EVENT_DETAIL,
	UCConfigInterface,
} from '@/libraries/usercentrics/types';

import { DataProcessingService } from '@/libraries/usercentrics/types';

import { SnowplowCMPContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from './tracking/tracking-helper';

const UC_SCRIPT_ID = 'usercentrics-cmp';

export const LocalDataProcessingServices: Record<DataProcessingService, { name: string; id: string }> = {
	[DataProcessingService.DOUBLECLICK_AD]: { name: 'DoubleClick Ad', id: '9V8bg4D63' },
	[DataProcessingService.DOUBLECLICK_FLOODLIGHT]: { name: 'DoubleClick Floodlight', id: 'ByzZ5EsOsZX' },
	[DataProcessingService.FACEBOOK_PIXEL]: { name: 'Facebook Pixel', id: 'ko1w5PpFl' },
	[DataProcessingService.FACEBOOK_SOCIAL_PLUGINS]: { name: 'Facebook Social Plugins', id: 'XYQZBUojc' },
	[DataProcessingService.FIREBASE_AUTH]: { name: 'Firebase Authentication', id: 'smOQ7x8wu' },
	[DataProcessingService.GOOGLE_ADS]: { name: 'Google Ads', id: 'S1_9Vsuj-Q' },
	[DataProcessingService.GOOGLE_ADSENSE]: { name: 'Google AdServices', id: 'rJ99c4oOo-X' },
	[DataProcessingService.GOOGLE_ANALYTICS]: { name: 'Google Analytics', id: 'HkocEodjb7' },
	[DataProcessingService.GOOGLE_FONTS]: { name: 'Google Fonts', id: 'HkPBYFofN' },
	[DataProcessingService.GOOGLE_TAG_MANAGER]: { name: 'Google Tag Manager', id: 'BJ59EidsWQ' },
	[DataProcessingService.GSTATIC]: { name: 'gstatic.com', id: 'rJJjcVouoZ7' },
	[DataProcessingService.JUSTWATCH]: { name: 'JustWatch', id: 'qGy9kBiI8' },
	[DataProcessingService.PINTEREST]: { name: 'Pinterest', id: '9Q2qrmB3' },
	[DataProcessingService.RECAPTCHA]: { name: 'reCAPTCHA', id: 'Hko_qNsui-Q' },
	[DataProcessingService.SENTRY]: { name: 'Sentry', id: 'rH1vNPCFR' },
	[DataProcessingService.SNAPCHAT_PIXEL]: { name: 'Snapchat Pixel', id: '5b32c5b6-8e7f-4495-93ab-0061758f9a18' },
	[DataProcessingService.SPOTX]: { name: 'SpotX', id: 'ByLolcNs_i-m' },
	[DataProcessingService.TIKTOK]: { name: 'TikTok', id: 'Z0TcXjY0P' },
	[DataProcessingService.TWITTER_ANALYTICS]: { name: 'Twitter Analytics', id: 'S1hmcVouiZm' },
	[DataProcessingService.USERCENTRICS_CMP]: { name: 'Usercentrics Consent Management Platform', id: 'H1Vl5NidjWX' },
	[DataProcessingService.YOUTUBE_VIDEOS]: { name: 'YouTube Video', id: 'BJz7qNsdj-7' },
	[DataProcessingService.ZENDESK]: { name: 'Zendesk', id: '54s8nFgf' },
	[DataProcessingService.MARFEEL]: { name: 'Marfeel', id: 'IKEv1F3uL' },
};

export const UsercentricsHelper = {
	loadPromise: null as Promise<boolean> | null,
	preload(UCConfig: UCConfigInterface): Promise<boolean> {
		if (!isUsercentricsEnabled()) return Promise.resolve(false);

		// @todo check to UC_UI.restartCMP() when bestEffortId changes
		if (!this.loadPromise) {
			this.loadPromise = loadUI(UCConfig);
		}

		return this.loadPromise;
	},

	async initialize(UCConfig: UCConfigInterface): Promise<DataProcessingServiceConsent[]> {
		if (!isUsercentricsEnabled()) return [];

		// Reuses the script already injected by preload() if it ran earlier; otherwise loads it now.
		await this.preload(UCConfig);

		return getServicesConsents();
	},

	/**
	 * whenever a Usercentrics UI event happens, we sync the service consents from UC with our local state through the callback.
	 */
	registerAndTrackUsercentricEvents(saveCallback: (dpsConsents: DataProcessingServiceConsent[]) => void) {
		window.addEventListener('UC_UI_CMP_EVENT', async evt => {
			const detail = (<CustomEvent>evt).detail as UC_UI_EVENT_DETAIL;
			const label = getSourceLabel(detail?.source?.toLowerCase());

			const getConsentedServices = (consents: DataProcessingServiceConsent[]) =>
				JSON.stringify(consents.filter(({ status }) => status).map(({ name, id }) => ({ name, id })));

			let consents;
			switch (detail.type) {
				case 'CMP_SHOWN':
					TrackingHelper.trackEvent('cmp_consent', {
						action: 'seen',
						label: 'first_layer',
						nonInteraction: true,
					});
					break;
				case 'MORE_INFORMATION_LINK':
					TrackingHelper.trackEvent('cmp_consent', {
						action: 'seen',
						label: 'second_layer',
						nonInteraction: true,
					});
					break;
				case 'ACCEPT_ALL':
					{
						// save consents from 3rd party modal
						consents = await getServicesConsents();
						saveCallback(consents);
						// track save event
						// JSONed array name and id of all consents given
						const cmpContext = new SnowplowCMPContext(getConsentedServices(consents));

						TrackingHelper.trackEvent(
							'cmp_consent',
							{
								action: 'all_saved',
								// UC_UI_API means that it's been triggered through window.__ucCmp like within the consent banner
								label: detail.source === 'UC_UI_API' ? 'banner' : label,
								nonInteraction: true,
								// amount of consents given
								value: consents.filter(consent => consent.status).length,
							},
							[cmpContext]
						);
					}
					break;

				// track single consents
				case 'SAVE':
					// save consents from 3rd party modal
					consents = await getServicesConsents();
					// UC CMP v3 doesn't set the consent type as EXPLICIT, when only the "SAVE" option is selected for the first session.
					// if you reload the page, `window.__ucCmp.getConsentDetails()` correctly shows the consents as EXPLICIT.
					// that's why it's okay to patch this field here and set it to EXCPLICIT in the first session.
					consents.forEach(consent => {
						consent.type = 'EXPLICIT';
						consent.hasAnswered = true;
					});
					saveCallback(consents);

					// track save event
					// JSONed array name and id of all consents given
					const cmpContext = new SnowplowCMPContext(getConsentedServices(consents));

					// track save event
					TrackingHelper.trackEvent(
						'cmp_consent',
						{
							action: 'saved',
							label,
							nonInteraction: true,
							// amount of consents given
							value: consents.filter(consent => consent.status).length,
							// JSONed array name and id of all consents given
							property: getConsentedServices(consents),
						},
						[cmpContext]
					);
					break;
				// GDPR "reject all" (DENY_ALL) and the CCPA banner's "OK" confirmation with the "Do Not Sell"
				// toggle on (DENY_ALL_EXPLICIT) both mean the user explicitly opted out - track that once,
				// then fall through to the shared save logic below.
				case 'DENY_ALL':
				case 'DENY_ALL_EXPLICIT':
					TrackingHelper.setUserOptedOut();

				// the CCPA banner's "OK" confirmation with the toggle off (accepting the default, i.e. not
				// opting out).
				case 'ACCEPT_EXPLICIT':
					consents = await getServicesConsents();
					// same first-session UC v3 timing quirk noted above for SAVE: the per-service consent.type
					// may still read IMPLICIT right after this event fires, even though the user just made an
					// explicit choice (accepted the default, or opted out) by confirming the CCPA banner.
					consents.forEach(consent => {
						consent.type = 'EXPLICIT';
						consent.hasAnswered = true;
					});
					saveCallback(consents);
					break;
			}
		});
	},

	async consentToAllServices(): Promise<void> {
		return window.__ucCmp.acceptAllConsents() ?? Promise.resolve();
	},
};

async function getServicesConsents() {
	const consentDetails = await window.__ucCmp.getConsentDetails();

	// note: UC CMP v3 (TCF mode) dispatches `UC_UI_CMP_EVENT` immediately when the user clicks ACCEPT_ALL or SAVE.
	// however, internal state persistence (e.g., updating localStorage/IndexedDB, generating the TCF string,
	// and internal consent structures) happens asynchronously *after* the event fires.
	// as a result, calling `getConsentDetails()` right away may return stale data.
	// a brief delay (or polling) until internal persistence completes is necessary to reliably retrieve the updated consent status.
	//
	// this behavior is consistent with the documented SDK flow, where promise-based APIs like `acceptAllForTCF()` resolve only once internal TCF state is fully updated.
	await new Promise(resolve => setTimeout(resolve, 100));

	const servicesConsents = Object.entries(consentDetails.services).map(([id, service]) => ({
		id,
		name: service.name,
		category: service.category,
		status: service.consent?.given ?? false,
		type: service.consent?.type ?? 'IMPLICIT',
		hasAnswered: service.consent?.type === 'EXPLICIT',
	}));

	// ensure that the ordering is deterministic alphabetically
	servicesConsents.sort((serviceA, serviceB) => serviceA.name.localeCompare(serviceB.name, 'en'));

	return servicesConsents;
}

function isUsercentricsEnabled() {
	return import.meta.client;
}

function loadUI({ configId, isTCFEnabled }: UCConfigInterface): Promise<boolean> {
	return new Promise((resolve, reject) => {
		try {
			if (isTCFEnabled) {
				const tcfScriptStub = document.createElement('script');
				tcfScriptStub.src = 'https://web.cmp.usercentrics.eu/tcf/stub.js';
				tcfScriptStub.type = 'text/javascript';

				document.head.appendChild(tcfScriptStub);
			}

			window.UC_UI_SUPPRESS_CMP_DISPLAY = false;
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.async = true;
			script.id = UC_SCRIPT_ID;
			script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';

			script.setAttribute('data-settings-id', configId);

			window.addEventListener('UC_UI_INITIALIZED', () => resolve(true));

			document.head.appendChild(script);
		} catch {
			reject(false);
		}
	});
}

function getSourceLabel(source: string) {
	switch (source) {
		case 'first':
		case 'first_layer':
			return 'first_layer';

		case 'second':
		case 'second_layer':
			return 'second_layer';
	}

	return 'none';
}
